import {
  ABILITY_COUNT,
  Abilities,
  Actor,
  AiController,
  Body,
  Collider,
  Dynamic,
  Explosive,
  Hazard,
  Instability,
  Kit,
  PlayerTag,
  Prop,
  Renderable,
  Static,
  Transform,
  Velocity,
  AbilityId,
  type CharacterKind,
  type Difficulty,
  type PlayerId,
  type PropKind,
} from '@/components'
import type { Entity } from '@/core/ecs/Entity'
import type { World } from '@/core/ecs/World'
import { Rng } from '@/core/Rng'
import { ABILITIES, FIGHTER_KITS } from '@/game/abilities/definitions'
import type { HazardSpec, PropSpec, SpawnPoint } from '@/game/arenas/Arena'
import { MatchResource, PLAYER_COLORS, worldScale, type Controller } from '@/game/resources'
import { SceneBridgeResource } from '@/gfx/SceneBridge'
import { InputResource, resetInput } from '@/input/InputManager'

/** Physical profile of each prop kind — the single place these numbers live. */
interface PropProfile {
  shape: 'circle' | 'box'
  radius: number
  halfX: number
  halfZ: number
  mass: number
  drag: number
  restitution: number
  isStatic: boolean
  absorb: number
  explosive: boolean
}

const PROP_PROFILES: Record<PropKind, PropProfile> = {
  crate: {
    shape: 'circle',
    radius: 0.55,
    halfX: 0.55,
    halfZ: 0.55,
    mass: 0.6,
    drag: 4.5,
    restitution: 0.35,
    isStatic: false,
    absorb: 0,
    explosive: false,
  },
  barrel: {
    shape: 'circle',
    radius: 0.46,
    halfX: 0.46,
    halfZ: 0.46,
    mass: 0.5,
    drag: 4.2,
    restitution: 0.4,
    isStatic: false,
    absorb: 0,
    explosive: true,
  },
  tree: {
    shape: 'circle',
    radius: 0.5,
    halfX: 0.5,
    halfZ: 0.5,
    mass: 0,
    drag: 0,
    restitution: 0.25,
    isStatic: true,
    absorb: 0,
    explosive: false,
  },
  car: {
    shape: 'box',
    radius: 2.4,
    halfX: 2.3,
    halfZ: 1.02,
    mass: 0,
    drag: 0,
    restitution: 0.2,
    isStatic: true,
    absorb: 0,
    explosive: false,
  },
  pillar: {
    shape: 'circle',
    radius: 0.3,
    halfX: 0.3,
    halfZ: 0.3,
    mass: 0,
    drag: 0,
    restitution: 0.3,
    isStatic: true,
    absorb: 0,
    explosive: false,
  },
  barrier: {
    shape: 'circle',
    radius: 0.62,
    halfX: 0.62,
    halfZ: 0.62,
    mass: 0,
    drag: 0,
    restitution: 0,
    isStatic: true,
    // Not a wall: crossing a guard rail costs 20 % of your speed and nothing more.
    absorb: 0.2,
    explosive: false,
  },
  block: {
    shape: 'box',
    radius: 1.6,
    halfX: 1.55,
    halfZ: 1.55,
    mass: 0,
    drag: 0,
    restitution: 0.25,
    isStatic: true,
    absorb: 0,
    explosive: false,
  },
}

export function spawnPlayer(
  world: World,
  player: PlayerId,
  spawn: SpawnPoint,
  options: { model?: CharacterKind; ai?: Difficulty | null } = {},
): Entity {
  const gfx = world.tryResource(SceneBridgeResource)
  const color = PLAYER_COLORS[player]
  const e = world.createEntity()

  world.add(e, Transform, {
    x: spawn.x,
    z: spawn.z,
    angle: spawn.angle,
    prevX: spawn.x,
    prevZ: spawn.z,
    prevAngle: spawn.angle,
  })
  world.add(e, Velocity)
  // Fighters are deliberately chunky relative to the arena: at the old 0.45 m
  // a character read as a dot on a fixed overhead camera.
  world.add(e, Collider, { shape: 'circle', radius: 0.75 })
  // Knockback decay. Lowered from 4.0 after the second playtest: a hit has to
  // keep sliding long enough for the player to see that it landed. Combined
  // with the stronger impulses this roughly doubles how far a clean read moves
  // someone, which is the whole feel of the game.
  world.add(e, Body, { mass: 1, invMass: 1, restitution: 0.55, drag: 3 })
  // Movement scales with the platform. At 7 m/s crossing the default arena took
  // five seconds, and after the maps were enlarged twice on request it took
  // longer still — which is what "передвижение медленное" measures. 9.5 m/s
  // crosses the authored slab in under four, and the same holds at every size
  // because the speed rides `worldScale`.
  //
  // Friction went 8 → 15 in the same pass: at 8 the fighter coasted 0.79 m over
  // 0.42 s after the stick was released, against 0.17 s to reach full speed.
  // Instant to start and slow to stop is the definition of skating on ice.
  const scale = worldScale(world)
  const maxSpeed = 9.5 * scale
  const accel = 62 * scale
  world.add(e, Actor, {
    player,
    maxSpeed,
    accel,
    baseSpeed: maxSpeed,
    baseAccel: accel,
    friction: 15,
  })
  world.add(e, Instability, { value: 0, max: 100, sinceHit: 99 })
  world.add(e, PlayerTag, { player, color })
  world.addTag(e, Dynamic)

  // The kit is chosen with the model, so picking a fighter picks how they play
  // as well as how they look. Everyone keeps all five buttons; what changes is
  // their weight and their tempo. See `FIGHTER_KITS`.
  const kind = options.model ?? 'guard'
  const kit = FIGHTER_KITS[kind]
  world.add(e, Kit, {
    pushPower: kit.pushPower,
    boltSpeed: kit.boltSpeed,
    wavePower: kit.wavePower,
    waveRadius: kit.waveRadius,
    dashPower: kit.dashPower,
    vortexPower: kit.vortexPower,
    vortexRadius: kit.vortexRadius,
    braceMul: kit.braceMul,
    braceHold: kit.braceHold,
  })

  const abilities = world.add(e, Abilities)
  for (let i = 0; i < ABILITY_COUNT; i++) {
    const charges = kit.charges[i as AbilityId] ?? ABILITIES[i].maxCharges
    abilities.maxCharges[i] = charges
    abilities.charges[i] = charges
    abilities.cooldown[i] = kit.cooldown[i as AbilityId] ?? ABILITIES[i].cooldown
    abilities.recharge[i] = 0
    abilities.buffer[i] = 0
  }

  if (options.ai !== null && options.ai !== undefined) addAi(world, e, player, options.ai)

  if (gfx) {
    const model = options.model ?? 'guard'
    world.add(e, Renderable, { objectId: gfx.addCharacter(player, color, model) })
  }
  return e
}

function addAi(world: World, e: Entity, player: PlayerId, difficulty: Difficulty): void {
  // Seeded per slot so the two bots never march in lockstep, and separate
  // from the simulation's RNG so a bot can never perturb it.
  world.add(e, AiController, {
    difficulty,
    rng: new Rng(0x5ca1ab1e + player * 0x9e3779b9),
  })
}

/**
 * Hands a slot to the AI or back to a human, in the middle of a round too.
 *
 * Online, a player whose connection drops is taken over by a bot on the spot,
 * with the same body, charges and instability, and gets it back on return.
 * The slot's input starts idle either way, so neither side inherits a stick
 * the other was holding.
 */
export function setController(world: World, slot: PlayerId, controller: Controller): void {
  const match = world.resource(MatchResource)
  match.controllers[slot] = controller
  const input = world.tryResource(InputResource)?.players[slot]
  if (input) resetInput(input)

  const e = match.players[slot]
  if (e === undefined || !world.isAlive(e) || !world.has(e, PlayerTag)) return
  if (controller === 'ai') {
    if (!world.has(e, AiController)) addAi(world, e, slot, match.difficulty)
  } else {
    world.remove(e, AiController)
  }
}

export function spawnProp(world: World, spec: PropSpec): Entity {
  const gfx = world.tryResource(SceneBridgeResource)
  const profile = PROP_PROFILES[spec.kind]
  const scale = spec.scale ?? 1
  const e = world.createEntity()

  world.add(e, Transform, {
    x: spec.x,
    z: spec.z,
    angle: spec.angle ?? 0,
    prevX: spec.x,
    prevZ: spec.z,
    prevAngle: spec.angle ?? 0,
  })
  world.add(e, Collider, {
    shape: profile.shape,
    radius: profile.radius * scale,
    halfX: profile.halfX * scale,
    halfZ: profile.halfZ * scale,
    angle: spec.angle ?? 0,
  })
  world.add(e, Prop, { kind: spec.kind, seed: spec.seed ?? 0 })

  if (profile.isStatic) {
    world.add(e, Body, {
      mass: 0,
      invMass: 0,
      isStatic: true,
      restitution: profile.restitution,
      absorb: profile.absorb,
    })
    world.addTag(e, Static)
  } else {
    const mass = profile.mass * scale
    world.add(e, Body, {
      mass,
      invMass: 1 / mass,
      restitution: profile.restitution,
      drag: profile.drag,
    })
    world.add(e, Velocity)
    world.addTag(e, Dynamic)
  }

  if (profile.explosive) {
    world.add(e, Explosive, {
      // Base 22, scaled by the victim's instability in `explode` — the barrel
      // row of the launch table in docs/PHYSICS.md §6.2. The threshold sits
      // above the body-ram ceiling on purpose: a barrel goes off because
      // someone aimed something at it, never because two fighters collided.
      threshold: 12,
      radius: 4,
      power: 22,
      instability: 10,
      armed: true,
      fuse: 0,
    })
  }

  if (gfx) {
    world.add(e, Renderable, {
      objectId: gfx.addProp(spec.kind, spec.seed ?? 0, scale),
    })
  }
  return e
}

/** Default behaviour per hazard kind; a spec may override any of it. */
const HAZARD_PROFILES: Record<
  HazardSpec['kind'],
  { radius: number; power: number; instability: number; interval: number
    telegraph: number; pull: number; swirl: number }
> = {
  // Hazard instability is deliberately below what players inflict. At +9 and
  // +16 the arena was driving the escalation instead of the fighters: standing
  // near the centre of КРЕСТ added more instability than landing four Pushes.
  burner: {
    radius: 3.2, power: 14, instability: 6, interval: 3.5, telegraph: 0.8,
    pull: 0, swirl: 0,
  },
  // Wide and frequent, but it barely moves you: electro is about piling on
  // instability so the *next* real hit sends you into the void.
  electro: {
    radius: 4.6, power: 9, instability: 8, interval: 4.2, telegraph: 1,
    pull: 0, swirl: 0,
  },
  vortex: {
    radius: 8, power: 0, instability: 0, interval: 0, telegraph: 0,
    pull: 4, swirl: 0,
  },
  tornado: {
    radius: 4, power: 0, instability: 0, interval: 0, telegraph: 0,
    pull: 7, swirl: 9,
  },
}

export function spawnHazard(world: World, spec: HazardSpec): Entity {
  const gfx = world.tryResource(SceneBridgeResource)
  const profile = HAZARD_PROFILES[spec.kind]
  const e = world.createEntity()
  const radius = spec.radius ?? profile.radius

  world.add(e, Transform, { x: spec.x, z: spec.z, prevX: spec.x, prevZ: spec.z })
  world.add(e, Hazard, {
    kind: spec.kind,
    radius,
    power: spec.power ?? profile.power,
    instability: profile.instability,
    interval: spec.interval ?? profile.interval,
    telegraph: profile.telegraph,
    // Phase offset keeps multiple hazards from firing in lockstep.
    timer: spec.phase ?? 0,
    pull: spec.pull ?? profile.pull,
    swirl: profile.swirl,
  })

  if (gfx) {
    world.add(e, Renderable, { objectId: gfx.addHazard(spec.kind, radius) })
  }
  return e
}
